import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="copyable"
export default class extends Controller {
  static targets = ["copyable", "icon", "hint", "status"]
  static values = { variant: String }

  connect() {
    if (this.hasIconTarget) {
      this.originalIconPath = this.iconTarget.src
    }
  }

  disconnect() {
    this.copyRequest = null
    this.resetFeedback()
  }

  stopToggle(event) {
    if (this.variantValue === "contact" && (event.key === "Enter" || event.key === " ")) {
      event.stopPropagation()
    }
  }

  async copy(event) {
    if (this.variantValue === "contact") {
      event?.preventDefault()
      event?.stopPropagation()
    }

    const text = this.copyableTarget.dataset.copyText || this.copyableTarget.textContent.trim()
    // Only the latest copy on this connection may update feedback or run a fallback.
    const request = this.copyRequest = Symbol()
    let copied = false

    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text)
        copied = true
      } catch {
        // Fall through to the legacy clipboard path if this request is still current.
      }
    }

    if (this.copyRequest !== request || !this.element.isConnected) return
    if (!copied) copied = this.fallbackCopy(text)
    if (this.copyRequest !== request || !this.element.isConnected) return

    if (copied) {
      this.updateIcon()
    } else {
      this.resetFeedback()
      if (this.variantValue === "contact") {
        if (this.hasHintTarget) this.hintTarget.textContent = "Copy unavailable"
        if (this.hasStatusTarget) this.statusTarget.textContent = "Could not copy. Select the value and copy it manually."
      }
    }
  }

  fallbackCopy(text) {
    const previousFocus = document.activeElement
    const textArea = document.createElement("textarea")
    textArea.value = text
    textArea.style.position = "fixed"
    textArea.style.left = "-999999px"
    textArea.style.top = "-999999px"

    try {
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      return document.execCommand("copy") === true
    } catch {
      return false
    } finally {
      textArea.remove()
      previousFocus?.focus({ preventScroll: true })
    }
  }

  updateIcon() {
    clearTimeout(this.resetTimer)
    if (this.variantValue === "contact") {
      this.element.dataset.copied = "true"
      if (this.hasHintTarget) this.hintTarget.textContent = "Copied!"
      if (this.hasStatusTarget) this.statusTarget.textContent = "Copied to clipboard"
      this.resetTimer = setTimeout(() => this.resetFeedback(), 1800)
    } else if (this.hasIconTarget && this.iconTarget.dataset.completeIconPath) {
      this.iconTarget.src = this.iconTarget.dataset.completeIconPath
      this.resetTimer = setTimeout(() => this.resetFeedback(), 2000)
    }
  }

  resetFeedback() {
    clearTimeout(this.resetTimer)
    this.resetTimer = null
    if (this.variantValue === "contact") {
      delete this.element.dataset.copied
      if (this.hasHintTarget) this.hintTarget.textContent = "Copy"
      if (this.hasStatusTarget) this.statusTarget.textContent = ""
    } else if (this.hasIconTarget && this.originalIconPath) {
      this.iconTarget.src = this.originalIconPath
    }
  }
}
